import { scheduledTimeDisplay } from '../../../reporting_v2/helpers/scheduled-time-display'
import { NO_VALUE, formatCount, formatShortDateRange } from '../../math/format'
import { DateWindow } from '../../math/windows'
import { TopNotificationReportRow } from '../../data/contracts'
import { DashboardTemplate, TEMPLATE_SLOTS, primaryRankingMeasureFor } from '../../template'

export type RankingFloor = { measure: string; minimum: number; text: string }
export type RankingOption = { key: string; label: string; floor?: RankingFloor }

/** The rate ranking's name as a toggle label reads it, and as it starts a sentence. */
const RATE_RANKING_NAMES_BY_TEMPLATE: Record<DashboardTemplate, { label: string; sentence: string }> = {
    publisher: { label: 'CTR', sentence: 'CTR' },
    ecommerce: { label: 'Purchase Rate', sentence: 'Purchase rate' },
}

const PRIMARY_RANKING_LABELS: Record<string, string> = {
    clicks: 'By Clicks',
    purchase_amount: 'By Revenue',
    purchases: 'By Purchases',
}

/** 10,000 reads as 10K in the minimum's sentence, where a figure on a tile would read 10.0K. */
const formatMinimum = (minimum: number): string => formatCount(minimum).replace(/\.0K$/, 'K')

/**
 * The two notification rankings a template offers. The first is the template's primary measure. The second is the template's rate, which only ranks notifications with enough volume behind them.
 */
export const rankingOptionsFor = (template: DashboardTemplate, mixedCurrency: boolean): RankingOption[] => {
    const { secondary, secondaryFloor } = TEMPLATE_SLOTS[template].rankings
    const primaryMeasure = primaryRankingMeasureFor(template, mixedCurrency)
    const primaryOption: RankingOption = { key: primaryMeasure, label: PRIMARY_RANKING_LABELS[primaryMeasure] }
    const rateName = RATE_RANKING_NAMES_BY_TEMPLATE[template]
    return [
        primaryOption,
        {
            key: secondary,
            label: `By ${rateName.label}`,
            floor: {
                ...secondaryFloor,
                text: `${rateName.sentence} ranking requires ${formatMinimum(secondaryFloor.minimum)}+ ${secondaryFloor.measure}`,
            },
        },
    ]
}

export type NotificationRow = {
    id: number | string
    title: string
    body: string
    imageUrl: string | null
    domainId: number
    domainName: string
    /** The raw source code, such as `FEED`, `CAMPAIGN` or `MANUAL`; the source badge names it. */
    source: string
    /** A journey notification is sent over a span, so it reads as the dashboard's range; any other as its send time. */
    sentLabel: string
    /** Null when the response carries no number for the measure: it is not known, which is not zero. */
    impressions: number | null
    clicks: number | null
    ctr: number | null
    purchases: number | null
    purchaseAmount: number | null
    purchaseRate: number | null
}

/** A journey's notifications carry this source code. */
const JOURNEY_SOURCE = 'CAMPAIGN'

/** The date and time in the zone it was scheduled for, followed by the zone code when it has no abbreviation. */
const sentLabelFor = (scheduledTime: string, scheduledTimeZone: string | undefined): string => {
    const { timestamp, timeText, zoneCode } = scheduledTimeDisplay(scheduledTime, scheduledTimeZone)
    return `Sent ${timestamp.format('MMM D')}, ${timeText}${zoneCode ? ` ${zoneCode}` : ''}`
}

/** A number, or a string holding one; null for anything else, so a measure the response lacks is not read as zero. */
const numberOf = (value: unknown): number | null => {
    const number =
        typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN
    return Number.isFinite(number) ? number : null
}

/**
 * A row as the insights response carries it. The columns named by prefix (`notification_title`) arrive nested
 * under it (`notification.title`); the flat names are read too, so a flattened row reads the same. A row without a
 * notification id or a numeric domain id is dropped: the notification could not be opened without both.
 */
export const notificationRowFrom = (row: TopNotificationReportRow, range: DateWindow): NotificationRow | null => {
    const id = row.notification?.id
    const domainId = numberOf(row.domain?.id)
    if (id === undefined || id === null || id === '' || domainId === null) return null
    const source = String(row.notification?.source ?? row.notification_source ?? 'MANUAL').toUpperCase()
    const scheduledTime = row.notification?.scheduled_time ?? row.notification_scheduled_time
    const scheduledTimeZone = row.notification?.scheduled_time_zone ?? row.notification_scheduled_time_zone
    return {
        id,
        title: row.notification?.title ?? '',
        body: row.notification?.body ?? '',
        imageUrl: row.notification?.image_url ?? null,
        domainId,
        domainName: row.domain?.display_name ?? '',
        source,
        sentLabel:
            source === JOURNEY_SOURCE
                ? formatShortDateRange(range)
                : scheduledTime
                  ? sentLabelFor(scheduledTime, scheduledTimeZone)
                  : NO_VALUE,
        impressions: numberOf(row.impressions),
        clicks: numberOf(row.clicks),
        ctr: numberOf(row.ctr_decimal),
        purchases: numberOf(row.purchases),
        purchaseAmount: numberOf(row.purchase_amount),
        purchaseRate: numberOf(row.purchase_rate_decimal),
    }
}
